import { useRef } from 'react';
import { motion, useInView, useScroll, useTransform } from 'framer-motion';
import { Instagram, ExternalLink } from 'lucide-react';

const INSTAGRAM_URL = "https://www.instagram.com/devanandvestment/";

// Static Instagram-style images (since we can't fetch from Instagram API without credentials)
const instagramPosts = [
  {
    id: 1,
    image: 'https://images.pexels.com/photos/5814563/pexels-photo-5814563.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
    alt: 'Luxury Saree Collection',
  },
  {
    id: 2,
    image: 'https://images.unsplash.com/photo-1767955694884-d4bf352c23c2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBJbmRpYW4lMjBsZWhlbmdhJTIwYnJpZGFsJTIwZmFzaGlvbnxlbnwwfHx8fDE3NzI5ODYwOTB8MA&ixlib=rb-4.1.0&q=85',
    alt: 'Designer Lehenga',
  },
  {
    id: 3,
    image: 'https://images.unsplash.com/photo-1767790693645-2373e54d4f02?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHwyfHxsdXh1cnklMjBJbmRpYW4lMjBsZWhlbmdhJTIwYnJpZGFsJTIwZmFzaGlvbnxlbnwwfHx8fDE3NzI5ODYwOTB8MA&ixlib=rb-4.1.0&q=85',
    alt: 'Evening Gown',
  },
  {
    id: 4,
    image: 'https://images.unsplash.com/photo-1760613129745-418b15f91d56?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHw0fHxsdXh1cnklMjBJbmRpYW4lMjBsZWhlbmdhJTIwYnJpZGFsJTIwZmFzaGlvbnxlbnwwfHx8fDE3NzI5ODYwOTB8MA&ixlib=rb-4.1.0&q=85',
    alt: 'Bridal Collection',
  },
  {
    id: 5,
    image: 'https://images.unsplash.com/photo-1610047614301-13c63f00c032?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHwyfHxJbmRpYW4lMjBjcm9wJTIwdG9wJTIwZXRobmljJTIwZmFzaGlvbnxlbnwwfHx8fDE3NzI5ODYwOTB8MA&ixlib=rb-4.1.0&q=85',
    alt: 'Crop Top Set',
  },
  {
    id: 6,
    image: 'https://images.pexels.com/photos/28943676/pexels-photo-28943676.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
    alt: 'Ethnic Wear',
  },
  {
    id: 7,
    image: 'https://images.unsplash.com/photo-1717835429626-d6dd3f2c9afb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNzl8MHwxfHNlYXJjaHwzfHxJbmRpYW4lMjBtb2RlbCUyMHNhcmVlJTIwbHV4dXJ5JTIwZmFzaGlvbiUyMHBvcnRyYWl0JTIwcmVkJTIwZ29sZHxlbnwwfHx8fDE3NzI5ODYwODZ8MA&ixlib=rb-4.1.0&q=85',
    alt: 'Premium Fashion',
  },
  {
    id: 8,
    image: 'https://images.unsplash.com/photo-1750481890787-964db32a0d74?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHwxfHxJbmRpYW4lMjBmYXNoaW9uJTIwdG9wc3xlbnwwfHx8fDE3NzI5ODYwOTB8MA&ixlib=rb-4.1.0&q=85',
    alt: 'Designer Tops',
  },
];

const InstagramCard = ({ post, index }) => {
  return (
    <motion.a
      href={INSTAGRAM_URL}
      target="_blank"
      rel="noopener noreferrer"
      className="relative flex-shrink-0 w-[250px] md:w-[300px] lg:w-[350px] aspect-square overflow-hidden group rounded-2xl"
      style={{ boxShadow: '0 10px 40px -10px rgba(128, 0, 32, 0.25), 0 0 20px rgba(212, 175, 55, 0.15)' }}
      initial={{ opacity: 0, x: 50 }}
      whileInView={{ opacity: 1, x: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      data-testid={`instagram-post-${post.id}`}
    >
      {/* Image */}
      <motion.img
        src={post.image}
        alt={post.alt}
        className="w-full h-full object-cover"
        whileHover={{ scale: 1.1 }}
        transition={{ duration: 0.6 }}
      />

      {/* Overlay on Hover */}
      <motion.div
        className="absolute inset-0 bg-[#800020]/80 flex items-center justify-center rounded-2xl"
        initial={{ opacity: 0 }}
        whileHover={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
      >
        <motion.div
          className="text-center"
          initial={{ y: 20 }}
          whileHover={{ y: 0 }}
        >
          <Instagram className="text-white mx-auto mb-3" size={32} />
          <span className="font-body text-white text-sm tracking-wider">
            View on Instagram
          </span>
        </motion.div>
      </motion.div>

      {/* Gold corner accent */}
      <div className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center bg-[#D4AF37]/80 rounded-full">
        <Instagram className="text-white" size={14} />
      </div>
    </motion.a>
  );
};

export const InstagramGallery = () => {
  const sectionRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: '-100px' });

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ['start end', 'end start'],
  });

  // Horizontal scroll animation based on vertical scroll
  const x = useTransform(scrollYProgress, [0, 1], ['0%', '-30%']);

  return (
    <section
      ref={sectionRef}
      className="py-24 md:py-32 bg-[#FDFBF7] overflow-hidden"
      data-testid="instagram-gallery-section"
    >
      {/* Section Header */}
      <motion.div
        className="text-center mb-16 px-4"
        initial={{ opacity: 0, y: 30 }}
        animate={isInView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.6 }}
      >
        <motion.div
          className="inline-flex items-center gap-3 mb-4"
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ delay: 0.2 }}
        >
          <Instagram className="text-[#800020]" size={24} />
          <span className="font-accent text-sm tracking-[0.3em] text-[#D4AF37] uppercase">
            @devanandvestment
          </span>
        </motion.div>
        <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl text-[#1A1A1A]">
          Follow Our Journey
        </h2>
        <motion.div
          className="w-24 h-0.5 bg-[#D4AF37] mx-auto mt-6"
          initial={{ scaleX: 0 }}
          animate={isInView ? { scaleX: 1 } : {}}
          transition={{ duration: 0.8, delay: 0.4 }}
        />
      </motion.div>

      {/* Horizontal Scrolling Gallery */}
      <motion.div
        ref={scrollContainerRef}
        className="flex gap-4 md:gap-6 px-4 md:px-8"
        style={{ x }}
      >
        {instagramPosts.map((post, index) => (
          <InstagramCard key={post.id} post={post} index={index} />
        ))}
      </motion.div>

      {/* Follow CTA */}
      <motion.div
        className="text-center mt-16 px-4"
        initial={{ opacity: 0, y: 20 }}
        animate={isInView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.6, delay: 0.6 }}
      >
        <motion.a
          href={INSTAGRAM_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-[#833AB4] via-[#FD1D1D] to-[#F77737] text-white font-body text-sm tracking-widest uppercase"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          data-testid="instagram-follow-btn"
        >
          <Instagram size={20} />
          <span>Follow Us on Instagram</span>
          <ExternalLink size={16} />
        </motion.a>
      </motion.div>
    </section>
  );
};
